/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormattedMessage } from 'react-intl';

import { ButtonGroup, Dialog, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import type { NaptimeError } from 'bundles/naptimejs';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const styles = {
  messageContainer: css`
    margin-bottom: var(--cds-spacing-400);
  `,
};

const { HeadingGroup, Content, Actions } = Dialog;

type Props = {
  onClose: () => void;
  onResendEmail: () => void;
  onCheckVerified: () => void;
  apiStatus?: string;
  error?: NaptimeError | null;
  emailAddress: string;
};

export const ProgramEmailVerifyModalWithoutProgramContext = ({
  onClose,
  onResendEmail,
  onCheckVerified,
  apiStatus,
  error,
  emailAddress,
}: Props) => {
  const busy = apiStatus === 'API_IN_PROGRESS' || apiStatus === 'API_BEFORE_SEND';
  const extraSmallBreakpoint = useMediaQuery(() => breakpoints.down('xs'));
  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="email-verify-dialog-content"
      color="darkBlue"
      variant="standard"
    >
      <HeadingGroup>{_t('Verify your email address')}</HeadingGroup>
      <Content id="email-verify-dialog-content">
        <div css={styles.messageContainer}>
          <Typography2 component="p">
            <FormattedMessage message={_t('You need to verify your email address to continue with enrollment.')} />
          </Typography2>
        </div>
        <Typography2 component="p">
          <FormattedMessage
            message={_t(
              'Check your inbox for a verification email sent to {emailAddress}. After verification, please refresh this page to continue.'
            )}
            emailAddress={<strong>{emailAddress}</strong>}
          />
        </Typography2>
        {error && (
          <Typography2 component="p" color="error">
            {_t('An error occurred, please try again later.')}
          </Typography2>
        )}
      </Content>

      <Actions>
        <ButtonGroup
          aria-label={_t('Verification actions')}
          primaryButtonProps={{
            children: _t('Resend verification email'),
            onClick: onResendEmail,
            disabled: busy,
            fullWidth: extraSmallBreakpoint,
          }}
          alternateButtonProps={{
            children: _t("Yes, I'm verified"),
            onClick: onCheckVerified,
            variant: 'secondary',
            disabled: busy,
            fullWidth: extraSmallBreakpoint,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default ProgramEmailVerifyModalWithoutProgramContext;
